<script setup lang="ts">
/**
 * `sbi.viz.documents` — result cards for document search rows.
 *
 * Generic across stores: every slot reads a category column of the rows
 * contract (`date`, `source`, `type`, `author`, `reference`, `status`,
 * `topics`, `people`), never a client's field names, and hides when the
 * store has no field of that category. Wording comes from `opts`, then the
 * doc_category's presentation hints (`row._search.presentation`), then
 * neutral defaults ("document", "{n} similar documents").
 *
 * Text is rendered as text: highlighting splits the passage into segments,
 * never `v-html`, so a passage can't inject markup.
 *
 * **"View"** — a button at the right of each card's title row — opens the
 * whole document in a panel (`DocumentViewer`). The label is generic; the
 * item noun is only in its accessible name. Offered only for a row carrying a `view_token` — the
 * batch route mints one per row it returned — inside an EmbedHost, outside
 * print, and unless `opts.view === false`. A title with a `uri` still links
 * out; one without opens the panel.
 */
import { computed, ref } from "vue";

import type { DocumentsOpts } from "../sbi/protocol";
import { useEmbedApi } from "../sbi/embed-context.ts";
import { usePrintMode } from "../sbi/print-mode.ts";
import DocumentViewer from "./DocumentViewer.vue";
import LucentSpinner from "./LucentSpinner.vue";
import { formatError } from "./errorText.ts";

interface Row {
  rank?: number;
  doc_id?: string;
  title?: string | null;
  passage?: string;
  uri?: string | null;
  view_token?: string;
  date?: unknown;
  source?: unknown;
  type?: unknown;
  author?: unknown;
  place?: unknown;
  reference?: unknown;
  status?: unknown;
  topics?: unknown;
  people?: unknown;
  also_ran?: number;
  copies?: Array<{ doc_id?: string; title?: string | null; date?: unknown; source?: unknown; type?: unknown; view_token?: string }>;
  excerpts?: Array<{ ordinal?: number; passage?: string }>;
  _search?: { question?: string; presentation?: { item?: string; group?: string; group_label?: string } | null };
}

const props = withDefaults(
  defineProps<{
    rows: unknown[];
    opts?: DocumentsOpts;
    loading?: boolean;
    errored?: boolean;
    exId?: string;
  }>(),
  { loading: false, errored: false },
);

const PASSAGE_CHARS = 520;
const STOPWORDS = new Set([
  "the", "and", "for", "with", "that", "this", "from", "what", "when", "where", "which",
  "who", "how", "why", "are", "was", "were", "has", "have", "had", "about", "into", "over",
  "did", "does", "said", "say", "says", "its", "their", "they", "them", "our", "your",
]);

const rows = computed<Row[]>(() => (props.rows ?? []).filter((r): r is Row => !!r && typeof r === "object"));
const first = computed<Row | undefined>(() => rows.value[0]);
const hints = computed(() => first.value?._search?.presentation ?? null);
const item = computed(() => props.opts?.item ?? hints.value?.item ?? "document");
const groupMode = computed<"duplicates" | "excerpts" | "none">(() => {
  const g = props.opts?.group ?? hints.value?.group;
  if (g === "duplicates" || g === "excerpts" || g === "none") return g;
  return rows.value.some((r) => (r.excerpts?.length ?? 0) > 0) ? "excerpts" : "duplicates";
});
const compact = computed(() => props.opts?.variant === "compact");

const toTime = (v: unknown): number => {
  if (typeof v === "number") return v > 1e12 ? v : v * 1000;
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isNaN(t) ? 0 : t;
  }
  return 0;
};

const sorted = computed<Row[]>(() => {
  const list = [...rows.value];
  if (props.opts?.sort === "date") list.sort((a, b) => toTime(b.date) - toTime(a.date));
  return list;
});

const limit = computed(() => props.opts?.limit ?? (compact.value ? 5 : 10));
const showAll = ref(false);
const visible = computed(() => (showAll.value ? sorted.value : sorted.value.slice(0, limit.value)));
const hidden = computed(() => Math.max(0, sorted.value.length - visible.value.length));

const isEmpty = computed(() => !props.loading && !props.errored && rows.value.length === 0);
const emptyText = computed(() => props.opts?.empty ?? `No matching ${item.value}s.`);
const errorText = computed(() => formatError("Couldn't load these results", props.exId));

/** Question terms worth highlighting: 3+ letters, not stopwords. */
const terms = computed<string[]>(() => {
  if (props.opts?.highlight === false) return [];
  const q = first.value?._search?.question ?? "";
  return [...new Set(q.toLowerCase().match(/[\p{L}\p{N}]{3,}/gu) ?? [])].filter((t) => !STOPWORDS.has(t));
});

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Passage → text segments, marking any word that starts with a question term. */
const segments = (text: string | undefined): Array<{ text: string; mark: boolean }> => {
  let body = (text ?? "").trim();
  if (body.length > PASSAGE_CHARS) body = `${body.slice(0, PASSAGE_CHARS).replace(/\s+\S*$/, "")}…`;
  if (terms.value.length === 0 || body === "") return [{ text: body, mark: false }];
  const re = new RegExp(`(${terms.value.map(escapeRe).join("|")})[\\p{L}\\p{N}]*`, "giu");
  const out: Array<{ text: string; mark: boolean }> = [];
  let last = 0;
  for (const m of body.matchAll(re)) {
    const at = m.index ?? 0;
    // Only at a word start, so "pay" doesn't light up inside "repay".
    if (at > 0 && /[\p{L}\p{N}]/u.test(body[at - 1]!)) continue;
    if (at > last) out.push({ text: body.slice(last, at), mark: false });
    out.push({ text: m[0], mark: true });
    last = at + m[0].length;
  }
  if (last < body.length) out.push({ text: body.slice(last), mark: false });
  return out;
};

const text = (v: unknown): string | null =>
  v === null || v === undefined || v === "" ? null : Array.isArray(v) ? v.join(", ") : String(v);

const formatDate = (v: unknown): string | null => {
  const t = toTime(v);
  if (!t) return text(v);
  return new Date(t).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

const safeUri = (uri: string | null | undefined): string | null =>
  typeof uri === "string" && /^https?:\/\//i.test(uri) ? uri : null;

const list = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x) => x !== null && x !== "").map(String) : text(v) ? [String(v)] : [];

/** Badge colour from the type value, so no store's types are hard-coded. */
const BADGE_HUES = [212, 32, 280, 150, 350, 190, 50, 255];
const badgeStyle = (v: unknown) => {
  const s = String(v);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = BADGE_HUES[h % BADGE_HUES.length]!;
  return { background: `hsl(${hue} 70% 95%)`, color: `hsl(${hue} 45% 38%)` };
};

const statusTone = (v: unknown): string => {
  const s = String(v).toLowerCase();
  if (/(supersed|repeal|withdraw|archiv|expired|lapsed|rescind)/.test(s)) return "old";
  if (/(draft|pending|proposed|review|open)/.test(s)) return "warn";
  return "ok";
};

const groupCount = (r: Row): number =>
  groupMode.value === "excerpts" ? (r.excerpts?.length ?? 0) : groupMode.value === "duplicates" ? (r.also_ran ?? 0) : 0;

const groupLabel = (r: Row): string => {
  const n = groupCount(r);
  const template =
    props.opts?.groupLabel ??
    hints.value?.group_label ??
    (groupMode.value === "excerpts"
      ? `+{n} more ${n === 1 ? "excerpt" : "excerpts"} from this ${item.value}`
      : `{n} similar ${n === 1 ? item.value : `${item.value}s`}`);
  return template.replace(/\{n\}/g, String(n));
};

const byline = (r: Row): string[] =>
  [text(r.source), text(r.author) ?? (list(r.people).length ? peopleText(r.people) : null), formatDate(r.date), text(r.place)].filter(
    (x): x is string => !!x,
  );

/** "A, B, C +2 more": a list as one badge's value. */
const joined = (values: string[], cap: number): string =>
  values.slice(0, cap).join(", ") + (values.length > cap ? ` +${values.length - cap} more` : "");

const embedApi = useEmbedApi();
const isPrint = usePrintMode();

/** What the viewer is showing, or null when closed. */
const viewing = ref<{ docId: string; viewToken: string; title?: string | null; byline: string[] } | null>(null);

const canView = (token: string | undefined, docId: string | undefined): boolean =>
  props.opts?.view !== false && !isPrint() && embedApi() !== null && !!token && !!docId;

const openRow = (r: Row) => {
  if (!canView(r.view_token, r.doc_id)) return;
  viewing.value = { docId: r.doc_id!, viewToken: r.view_token!, title: r.title, byline: byline(r) };
};

type Copy = NonNullable<Row["copies"]>[number];
const openCopy = (c: Copy) => {
  if (!canView(c.view_token, c.doc_id)) return;
  const parts = [text(c.source), text(c.type), formatDate(c.date)].filter((x): x is string => !!x);
  viewing.value = { docId: c.doc_id!, viewToken: c.view_token!, title: c.title, byline: parts };
};

const peopleText = (v: unknown): string => {
  const p = list(v);
  return p.length > 3 ? `${p.slice(0, 3).join(", ")} +${p.length - 3}` : p.join(", ");
};
</script>

<template>
  <section
    class="docs"
    :class="{ 'is-loading': loading, 'is-errored': errored, 'is-compact': compact }"
    aria-label="Document search results"
  >
    <header v-if="opts?.title || opts?.description" class="docs-head">
      <h3 v-if="opts?.title" class="docs-title">{{ opts.title }}</h3>
      <p v-if="opts?.description" class="docs-desc">{{ opts.description }}</p>
    </header>

    <div v-if="loading" class="docs-spinner"><LucentSpinner :size="36" /></div>
    <div v-else-if="errored" class="docs-placeholder docs-placeholder--error">{{ errorText }}</div>
    <div v-else-if="isEmpty" class="docs-placeholder">{{ emptyText }}</div>

    <template v-else>
      <article v-for="r in visible" :key="`${r.doc_id}:${r.rank}`" class="doc">
        <div
          v-if="!compact && (text(r.type) || text(r.reference) || text(r.status))"
          class="kicker"
        >
          <span v-if="text(r.type)" class="badge" :style="badgeStyle(r.type)"><span class="badge-label">Type:</span> {{ text(r.type) }}</span>
          <span v-if="text(r.reference)" class="ref"><span class="badge-label">Reference:</span> {{ text(r.reference) }}</span>
          <span v-if="text(r.status)" class="status" :class="statusTone(r.status)"><span class="badge-label">Status:</span> {{ text(r.status) }}</span>
        </div>

        <div class="title-row">
          <h4 class="title">
            <a v-if="safeUri(r.uri)" :href="safeUri(r.uri)!" target="_blank" rel="noopener noreferrer">
              {{ r.title || "(untitled)" }}<span class="ext" aria-hidden="true">↗</span>
            </a>
            <button
              v-else-if="canView(r.view_token, r.doc_id)"
              type="button"
              class="title-open"
              @click="openRow(r)"
            >{{ r.title || "(untitled)" }}</button>
            <template v-else>{{ r.title || "(untitled)" }}</template>
          </h4>
          <!-- Generic on purpose: the category's noun ("story") reads oddly on
               a button; it stays in the accessible name and the group footer. -->
          <button
            v-if="!compact && canView(r.view_token, r.doc_id)"
            type="button"
            class="view"
            :aria-label="`View ${item}: ${r.title || '(untitled)'}`"
            @click="openRow(r)"
          >View</button>
        </div>

        <div v-if="byline(r).length" class="byline">
          <template v-for="(part, i) in byline(r)" :key="i">
            <span v-if="i > 0" class="dot" aria-hidden="true">·</span>
            <span :class="{ src: i === 0 && text(r.source) }">{{ part }}</span>
          </template>
          <span v-if="compact && text(r.type)" class="badge badge--inline" :style="badgeStyle(r.type)"><span class="badge-label">Type:</span> {{ text(r.type) }}</span>
        </div>

        <template v-if="!compact">
          <p v-if="r.passage" class="excerpt">
            <template v-for="(seg, i) in segments(r.passage)" :key="i">
              <mark v-if="seg.mark">{{ seg.text }}</mark><template v-else>{{ seg.text }}</template>
            </template>
          </p>

          <div v-if="list(r.topics).length" class="tags">
            <span class="tag"><span class="badge-label">{{ list(r.topics).length === 1 ? "Topic" : "Topics" }}:</span> {{ joined(list(r.topics), 6) }}</span>
          </div>

          <details v-if="groupCount(r) > 0" class="group">
            <summary>{{ groupLabel(r) }}</summary>
            <ul v-if="groupMode === 'duplicates'">
              <li v-for="c in r.copies ?? []" :key="c.doc_id">
                <b>{{ text(c.source) ?? c.title ?? c.doc_id }}</b>
                <template v-if="text(c.type)"> · {{ text(c.type) }}</template>
                <template v-if="formatDate(c.date)"> · {{ formatDate(c.date) }}</template>
                <button
                  v-if="canView(c.view_token, c.doc_id)"
                  type="button"
                  class="copy-view"
                  @click="openCopy(c)"
                >View</button>
              </li>
              <li v-if="(r.also_ran ?? 0) > (r.copies?.length ?? 0)" class="more-copies">
                and {{ (r.also_ran ?? 0) - (r.copies?.length ?? 0) }} more
              </li>
            </ul>
            <ul v-else>
              <li v-for="(e, i) in r.excerpts ?? []" :key="i">
                <p class="excerpt">
                  <span class="where">Excerpt {{ i + 2 }}</span>
                  <template v-for="(seg, j) in segments(e.passage)" :key="j">
                    <mark v-if="seg.mark">{{ seg.text }}</mark><template v-else>{{ seg.text }}</template>
                  </template>
                </p>
              </li>
            </ul>
          </details>
        </template>
      </article>

      <div v-if="hidden > 0" class="more">
        <button type="button" @click="showAll = true">Show {{ hidden }} more</button>
      </div>
    </template>

    <DocumentViewer
      v-if="viewing && embedApi()"
      :api="embedApi()!"
      :doc-id="viewing.docId"
      :view-token="viewing.viewToken"
      :title="viewing.title"
      :byline="viewing.byline"
      :item="item"
      @close="viewing = null"
    />
  </section>
</template>

<style scoped>
.docs {
  background: var(--card);
  border: var(--lucent-card-border, 1px solid var(--rule));
  border-radius: 10px;
  box-shadow: var(--lucent-card-shadow, 0 0 transparent);
  padding: var(--lucent-card-pad, var(--_lucent-card-pad, 16px 18px));
}
.docs-head { margin-bottom: 8px; }
.docs-title {
  font: var(
    --lucent-font-section-title,
    600 var(--_lucent-section-title-size, 16px)/1.25 system-ui, sans-serif
  );
  letter-spacing: var(--lucent-tracking-section-title, -0.015em);
  margin: 0;
}
.docs-desc { margin: 2px 0 0; font-size: 13px; color: var(--card-muted); }
.docs-spinner { display: flex; justify-content: center; padding: 28px 0; }
.docs-placeholder { padding: 24px 8px; text-align: center; font-size: 13px; color: var(--card-muted); }
.docs-placeholder--error { color: var(--lucent-chart-negative, #d96363); }

.doc { padding: 13px 0 14px; border-top: 1px solid var(--rule-soft, #efede6); }
.doc:first-of-type { border-top: 0; padding-top: 2px; }
.is-compact .doc { padding: 9px 0; }

.kicker { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-bottom: 4px; }
.badge {
  font: 600 9.5px/1 var(--mono, ui-monospace, monospace);
  letter-spacing: 0.08em; text-transform: uppercase;
  padding: 3px 6px; border-radius: 4px;
}
.badge--inline { margin-left: 2px; }
/* "Type:" / "Topic:" — the label half of a "label: value" badge, quieter than the value. */
.badge-label { font-weight: 400; opacity: 0.75; }
.badge .badge-label { text-transform: none; letter-spacing: 0; }
.ref { font: 600 11px var(--mono, ui-monospace, monospace); color: var(--lucent-chart-legend-color, #4a4641); min-width: 0; overflow-wrap: anywhere; }
.status { font: 600 10px/1 system-ui, sans-serif; padding: 3px 7px; border-radius: 999px; border: 1px solid; }
.status.ok { color: #2f8a6c; border-color: #b9e3d5; background: #effaf5; }
.status.warn { color: #9a6a12; border-color: #f0dca9; background: #fff8e8; }
.status.old { color: #8c8577; border-color: #dcd5c4; background: #f6f4ee; }

.title { font: 600 15px/1.35 system-ui, sans-serif; letter-spacing: -0.01em; margin: 0 0 3px; color: var(--lucent-chart-text-color, #2f2c26); overflow-wrap: anywhere; }
.is-compact .title { font-size: 13.5px; margin: 0; }
.title a { color: inherit; text-decoration: none; }
.title a:hover, .title a:focus-visible { text-decoration: underline; text-underline-offset: 2px; }
.ext { font-size: 11px; color: var(--card-muted); margin-left: 4px; }
.title-open {
  all: unset; cursor: pointer; color: inherit;
}
.title-open:hover, .title-open:focus-visible { text-decoration: underline; text-underline-offset: 2px; }
.title-open:focus-visible { box-shadow: 0 0 0 2px var(--lucent-focus, currentColor); border-radius: 2px; }
.title-row { display: flex; align-items: flex-start; gap: 12px; }
.title-row .title { flex: 1 1 auto; min-width: 0; }
/* Narrow: let "View" drop under a long title rather than squeeze it. */
@container (max-width: 479px) {
  .title-row { flex-wrap: wrap; }
}
.view {
  flex: 0 0 auto; cursor: pointer;
  font: 500 12px/1 system-ui, sans-serif; color: var(--lucent-link, #3b6fc0);
  background: var(--card, #fff); border: 1px solid var(--rule, #e5e3dc); border-radius: 6px;
  padding: 5px 10px; margin-top: 1px; touch-action: manipulation;
}
.view:hover { background: var(--rule-soft, #f6f4ee); }
.view:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--lucent-focus, currentColor); }
.copy-view {
  position: relative; touch-action: manipulation;
  border: 0; background: none; padding: 0; cursor: pointer; margin-left: 6px;
  font: 500 11.5px system-ui, sans-serif; color: var(--lucent-link, #3b6fc0);
}
.copy-view:hover, .copy-view:focus-visible { text-decoration: underline; text-underline-offset: 2px; outline: none; }

.byline { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: 12px; color: var(--card-muted); margin-bottom: 6px; overflow-wrap: anywhere; }
.is-compact .byline { margin: 2px 0 0; }
.byline .src { color: var(--lucent-chart-legend-color, #4a4641); font-weight: 600; }
.dot { color: #c9c3b4; }

.excerpt {
  margin: 0 0 7px; padding-left: 10px; border-left: 2px solid var(--rule, #e5e3dc);
  font-size: 13.5px; line-height: 1.55; color: var(--lucent-chart-legend-color, #4a4641);
  overflow-wrap: anywhere;
}
.excerpt mark { background: #fbeaa6; color: inherit; border-radius: 2px; padding: 0 1px; }
.where { display: block; font: 10.5px var(--mono, ui-monospace, monospace); color: var(--lucent-chart-label-color, #6a655b); margin-bottom: 2px; }

.tags { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 6px; }
/* 12px radius, not a pill: a long tag wraps, and a 999px radius turns
   a two-line pill into a lozenge. */
.tag { font-size: 11px; padding: 2px 8px; border-radius: 12px; border: 1px solid var(--rule, #e5e3dc); color: var(--lucent-chart-legend-color, #4a4641); max-width: 100%; box-sizing: border-box; overflow-wrap: anywhere; }

details.group summary { list-style: none; cursor: pointer; font-size: 13px; color: var(--lucent-link, #3b6fc0); display: inline-flex; gap: 5px; align-items: center; }
details.group summary::-webkit-details-marker { display: none; }
details.group summary::before { content: "▸"; font-size: 10px; transition: transform 0.15s; }
details.group[open] summary::before { transform: rotate(90deg); }
details.group ul { margin: 7px 0 0; padding: 0 0 0 12px; border-left: 2px solid var(--rule-soft, #efede6); list-style: none; }
details.group li { font-size: 12px; color: var(--card-muted); padding: 3px 0; }
details.group li b { color: var(--lucent-chart-legend-color, #4a4641); font-weight: 600; }
details.group li .excerpt { margin-top: 3px; font-size: 12.5px; }

.more { margin-top: 6px; text-align: center; }
.more button {
  border: 1px solid var(--rule, #e5e3dc); background: var(--card); border-radius: 8px;
  padding: 6px 14px; font: 500 12.5px system-ui, sans-serif; color: var(--lucent-chart-legend-color, #4a4641); cursor: pointer;
}
.more button:focus-visible { box-shadow: 0 0 0 2px var(--lucent-focus, currentColor); outline: none; }
@media (pointer: coarse) {
  .view, .more button, details.group summary { min-height: 40px; }
  details.group summary { padding-block: 8px; box-sizing: border-box; }
  .copy-view::after { content: ""; position: absolute; inset: -14px; }
}
</style>
